import React, { useState, useEffect } from 'react';
import Firebase, { FirebaseAuth } from 'EnoticeFirebase';
import { Modal } from 'lib/components/Modal';
import { FirebaseUser, ESnapshotExists, EUser } from 'lib/types';
import { PasswordField } from 'components/PasswordField';
import { passwordValidation } from 'lib/passwordValidators';

import ToastActions from 'redux/toast';
import { ColumnButton } from 'lib/components/ColumnButton';
import { useAppDispatch } from 'redux/hooks';

type ChangePasswordProps = {
  userAuth: FirebaseUser;
  user: ESnapshotExists<EUser>;
  closeChangePasswordModal: () => void;
};
export default function ChangePassword({
  userAuth,
  closeChangePasswordModal
}: ChangePasswordProps) {
  const dispatch = useAppDispatch();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isDisabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorAuth, setErrorAuth] = useState('');
  const [errorMatch, setErrorMatch] = useState(false);
  const [errorNewPassword, setErrorNewPassword] = useState('');
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const matchPassword = () => {
    if (newPassword && confirmNewPassword) {
      if (!passwordValidation.test(newPassword)) {
        setDisabled(true);
        return;
      }
      if (
        newPassword.length === confirmNewPassword.length &&
        newPassword === confirmNewPassword
      ) {
        setErrorMatch(false);
        if (newPassword.length > 8) {
          setDisabled(false);
          return true;
        }
      } else {
        setErrorMatch(true);
        setDisabled(true);
        return false;
      }
    }
  };
  const changePassword = () => {
    if (matchPassword()) {
      const { currentUser } = Firebase.auth();
      if (!currentUser || !currentUser.email) {
        console.error(`Current user does not exist ${currentUser?.uid}`);
        return;
      }

      setLoading(true);
      setDisabled(true);

      const credentials = FirebaseAuth.EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );

      userAuth
        .reauthenticateWithCredential(credentials)
        .then(() => {
          currentUser
            .updatePassword(newPassword)
            .then(() => {
              setPasswordUpdated(true);
              setLoading(false);
            })
            .catch((error: Error) => {
              setErrorAuth(error.message);
              setLoading(false);
              setDisabled(false);
            });
        })
        .catch((error: Error) => {
          setErrorAuth(error.message);
          setLoading(false);
          setDisabled(false);
        });
    }
  };

  useEffect(() => {
    matchPassword();
  }, [newPassword, confirmNewPassword]);

  useEffect(() => {
    if (passwordUpdated) {
      closeChangePasswordModal();
      dispatch(
        ToastActions.toastSuccess({
          headerText: 'Success!',
          bodyText: 'Password changed successfully'
        })
      );
    }
  }, [passwordUpdated]);

  useEffect(() => {
    if (newPassword) {
      if (newPassword.length < 9) {
        setErrorNewPassword('Password must be at least 9 characters');
        return;
      }
      if (!passwordValidation.test(newPassword)) {
        setErrorNewPassword(
          'Password must contain a number or a special character'
        );
        return;
      }
    }
    setErrorNewPassword('');
  }, [newPassword]);

  return (
    <Modal
      id="change-password-modal"
      title="Change your password"
      onClose={() => closeChangePasswordModal()}
      size="md"
    >
      <div className="space-y-4">
        <PasswordField
          id="currentPassword"
          required
          value={currentPassword}
          placeHolderText="Current password *"
          onValueChange={(value: string) => setCurrentPassword(value)}
          data-testid="password"
        />
        {errorAuth && (
          <div className="text-red-600 text-sm ml-1">{errorAuth}</div>
        )}
        <PasswordField
          id="password"
          required
          value={newPassword}
          placeHolderText="New password *"
          onValueChange={(value: string) => setNewPassword(value)}
          data-testid="newPassword"
        />
        {errorNewPassword && (
          <div className="text-red-600 text-sm ml-1">{errorNewPassword}</div>
        )}
        <PasswordField
          id="confirmNewPassword"
          required
          value={confirmNewPassword}
          placeHolderText="Confirm new password *"
          onValueChange={(value: string) => setConfirmNewPassword(value)}
          data-testid="confirm-new-password"
        />
        {errorMatch && (
          <div className="text-red-600 text-sm ml-1">
            Passwords do not match
          </div>
        )}

        <ColumnButton
          id="change-password"
          type="button"
          disabled={isDisabled || loading}
          onClick={changePassword}
          loading={loading}
          buttonText={'Set New Password'}
          primary
        />
      </div>
    </Modal>
  );
}
